import { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Pencil,
  Trash2,
  Copy,
  Pause,
  Play,
  CalendarClock,
  AlertTriangle,
} from 'lucide-react';
import { Section } from '@vnetwork/ui/layout';
import {
  Card,
  CardHeader,
  CardBody,
  Button,
  IconButton,
  Badge,
  Checkbox,
  Skeleton,
} from '@vnetwork/ui/primitives';
import { Stat, StatGroup, StatusPill, EmptyState } from '@vnetwork/ui/data';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useSchedules,
  useDeleteSchedule,
  useCreateSchedule,
  useReorderSchedule,
  type Channel,
  type Schedule,
} from '@/api/fastchannel';
import { trackFeatureAction } from '@/lib/analytics-events';
import {
  dayLabel,
  isToday,
  clockFromMs,
  stampFromMs,
  formatDurationHuman,
  formatDurationClock,
  liveProgram,
  scheduleStatus,
  scheduleTypeColor,
  scheduleTypeLabel,
  SCHEDULE_STATUS_TONE,
  SCHEDULE_STATUS_LABEL,
} from '../shared';
import {
  resolvePlayoutMode,
  isPlaylistMode,
  modeLoops,
  PLAYOUT_MODE_META,
} from '../playout-modes';
import { ScheduleDialog } from './ScheduleDialog';
import { PlaylistEditor } from './PlaylistEditor';
import styles from '../Fastchannel.module.css';

interface ScheduleTimelineProps {
  serviceUuid: string;
  channelUuid: string;
  channel: Channel;
}

const LEGEND: { label: string; color: string }[] = [
  { label: 'Live', color: scheduleTypeColor('live') },
  { label: 'VOD', color: scheduleTypeColor('vod') },
  { label: 'Ad', color: scheduleTypeColor('ads') },
];

/** The seam between two consecutive programs: 0, a gap, or an overlap. */
function gapBetween(a: Schedule, b: Schedule): number {
  return b.start_time - a.end_time;
}

export function ScheduleTimeline({ serviceUuid, channelUuid, channel }: ScheduleTimelineProps) {
  const { show } = useToast();
  const [date, setDate] = useState(() => new Date());
  const [paused, setPaused] = useState(false);
  const [dialog, setDialog] = useState<{ mode: 'create' | 'edit'; schedule?: Schedule } | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Schedule | null>(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Re-tick so the "now playing" program + offset advance with wall-clock.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setNow(Date.now()), 2000);
    return () => clearInterval(t);
  }, [paused]);

  const mode = useMemo(() => resolvePlayoutMode(channel), [channel]);
  const playlist = isPlaylistMode(mode);
  const loops = modeLoops(mode);

  // Controlplane returns the channel's full schedule set. In playlist modes the
  // API order *is* the playout order, so preserve it; in EPG mode we sort by
  // start time and filter to the selected day (no per-date server view).
  const { data, isLoading } = useSchedules(serviceUuid, channelUuid, paused);
  const rows = useMemo(() => {
    const all = data ?? [];
    if (playlist) return [...all];
    const sorted = [...all].sort((a, b) => a.start_time - b.start_time);
    const dayStart = dayjs(date).startOf('day').valueOf();
    const dayEnd = dayjs(date).endOf('day').valueOf();
    return sorted.filter((s) => s.start_time >= dayStart && s.start_time <= dayEnd);
  }, [data, date, playlist]);

  // Drop selections for rows that no longer exist.
  useEffect(() => {
    setSelected((sel) => {
      const ids = new Set((data ?? []).map((s) => s.id));
      const next = new Set([...sel].filter((id) => ids.has(id)));
      return next.size === sel.size ? sel : next;
    });
  }, [data]);

  const totalDuration = useMemo(() => rows.reduce((sum, s) => sum + (s.duration || 0), 0), [rows]);
  const hasOverlap = useMemo(
    () => rows.some((s, i) => i < rows.length - 1 && gapBetween(s, rows[i + 1]) < 0),
    [rows],
  );
  // The program actually on air (matches the packager's time-addressed playout,
  // including looped/repeat channels). Computed over the FULL schedule, not the
  // day-filtered rows, since the loop spans the whole lineup.
  const live = useMemo(
    () => liveProgram(data ?? [], loops, now, mode),
    [data, loops, now, mode],
  );

  const shiftDay = (days: number) => {
    setDate((d) => {
      const next = new Date(d);
      next.setDate(next.getDate() + days);
      return next;
    });
  };

  const create = useCreateSchedule(serviceUuid, channelUuid, {
    onSuccess: () => {
      show({ tone: 'success', title: 'Program duplicated' });
      trackFeatureAction('fastchannel', 'schedule', 'create');
    },
    onError: () => show({ tone: 'danger', title: 'Could not duplicate program' }),
  });
  const remove = useDeleteSchedule(serviceUuid, channelUuid, {
    onSuccess: () => {
      show({ tone: 'success', title: 'Program removed' });
      trackFeatureAction('fastchannel', 'schedule', 'delete');
      setPendingDelete(null);
    },
    onError: () => show({ tone: 'danger', title: 'Could not remove program' }),
  });
  const reorder = useReorderSchedule(serviceUuid, channelUuid, {
    onSuccess: () => trackFeatureAction('fastchannel', 'schedule', 'edit'),
    onError: () => show({ tone: 'danger', title: 'Could not save the new order' }),
  });

  // Duplicate appends a copy right after the last program (back-to-back).
  const duplicate = (s: Schedule) => {
    const startMs = rows[rows.length - 1]?.end_time ?? s.end_time;
    const endMs = startMs + s.duration * 1000;
    create.mutate({
      channel: channel.name,
      title: s.title ? `${s.title} (copy)` : 'Program (copy)',
      description: s.description,
      type: s.type,
      url: s.url ?? '',
      start_time: startMs,
      end_time: endMs,
      duration: s.duration,
      live_url: s.type === 'live' ? s.url : undefined,
    });
  };

  const toggleSelected = (id: string) =>
    setSelected((sel) => {
      const next = new Set(sel);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const bulkDelete = async () => {
    const ids = [...selected];
    try {
      await Promise.all(ids.map((id) => remove.mutateAsync(id)));
      show({ tone: 'success', title: `${ids.length} program${ids.length === 1 ? '' : 's'} removed` });
      trackFeatureAction('fastchannel', 'schedule', 'delete');
    } catch {
      show({ tone: 'danger', title: 'Some programs could not be removed' });
    }
    setSelected(new Set());
    setBulkDeleteOpen(false);
  };

  return (
    <Section
      title="Schedule"
      description={
        playlist
          ? 'Playlist for this channel, in playout order. Drag to reorder.'
          : 'Program timeline for the selected day, ordered by start time.'
      }
      actions={
        <Button startIcon={<Plus size={15} />} onClick={() => setDialog({ mode: 'create' })}>
          Add program
        </Button>
      }
    >
      <StatGroup columns={3} style={{ marginBottom: 'var(--vn-space-5)' }}>
        <Stat label="Programs" value={rows.length} />
        <Stat label="Total duration" value={formatDurationClock(totalDuration)} />
        <Stat label={playlist ? 'Mode' : 'Day'} value={playlist ? PLAYOUT_MODE_META[mode].label : dayLabel(date)} />
      </StatGroup>

      <Card>
        <CardHeader>
          <div className={styles.timelineBar}>
            <div className={styles.dayNav}>
              {playlist ? (
                <span className={styles.dayLabel}>{PLAYOUT_MODE_META[mode].label} playlist</span>
              ) : (
                <>
                  <IconButton variant="ghost" size="sm" aria-label="Previous day" onClick={() => shiftDay(-1)}>
                    <ChevronLeft size={16} />
                  </IconButton>
                  <span className={styles.dayLabel}>{dayLabel(date)}</span>
                  <IconButton variant="ghost" size="sm" aria-label="Next day" onClick={() => shiftDay(1)}>
                    <ChevronRight size={16} />
                  </IconButton>
                  {!isToday(date) && (
                    <Button variant="secondary" size="sm" onClick={() => setDate(new Date())}>
                      Today
                    </Button>
                  )}
                </>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-4)' }}>
              <div className={styles.legend}>
                {LEGEND.map((l) => (
                  <span key={l.label} className={styles.legendItem}>
                    <span className={styles.legendDot} style={{ background: l.color }} />
                    {l.label}
                  </span>
                ))}
              </div>
              <Button
                variant="ghost"
                size="sm"
                startIcon={paused ? <Play size={14} /> : <Pause size={14} />}
                onClick={() => setPaused((p) => !p)}
              >
                {paused ? 'Resume' : 'Auto-refresh'}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardBody>
          {selected.size > 0 && (
            <div className={styles.bulkBar}>
              <span>{selected.size} selected</span>
              <div style={{ display: 'flex', gap: 'var(--vn-space-2)' }}>
                <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>
                  Clear
                </Button>
                <Button variant="danger" size="sm" startIcon={<Trash2 size={14} />} onClick={() => setBulkDeleteOpen(true)}>
                  Delete selected
                </Button>
              </div>
            </div>
          )}

          {live && (
            <div className={styles.nowPlaying} style={{ marginBottom: 'var(--vn-space-4)' }}>
              <div>
                <div className={styles.nowTitle}>{live.program.title || 'Untitled program'}</div>
                <div className={styles.nowMeta}>
                  On air · {formatDurationClock(Math.floor(live.offsetMs / 1000))} /{' '}
                  {formatDurationClock(Math.floor((live.program.end_time - live.program.start_time) / 1000))} ·{' '}
                  ends in {formatDurationHuman(Math.max(0, Math.round((live.endsAtMs - now) / 1000)))}
                  {loops ? ' · looping' : ''}
                </div>
              </div>
              <Badge tone="success" appearance="subtle">
                On air
              </Badge>
            </div>
          )}

          {!playlist && hasOverlap && (
            <div className={`${styles.gapDivider} ${styles.gapOverlap}`} style={{ marginBottom: 'var(--vn-space-3)' }}>
              <AlertTriangle size={13} />
              Some programs overlap. Edit their start times to space them out.
            </div>
          )}

          {isLoading && rows.length === 0 ? (
            <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} style={{ height: 56 }} />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<CalendarClock size={20} />}
              title="No programs scheduled"
              description={
                playlist
                  ? 'This playlist is empty. Add a program to start the lineup.'
                  : `Nothing is scheduled for ${dayLabel(date).toLowerCase()}. Add a program to fill the timeline.`
              }
              actions={
                <Button startIcon={<Plus size={15} />} onClick={() => setDialog({ mode: 'create' })}>
                  Add program
                </Button>
              }
            />
          ) : playlist ? (
            <PlaylistEditor
              rows={rows}
              live={live}
              selected={selected}
              busy={create.isPending || reorder.isPending}
              onToggleSelect={toggleSelected}
              onEdit={(s) => setDialog({ mode: 'edit', schedule: s })}
              onDuplicate={duplicate}
              onDelete={setPendingDelete}
              onReorder={(ids) => reorder.mutate(ids)}
            />
          ) : (
            <div className={styles.scheduleList}>
              {rows.map((s, i) => {
                const status = scheduleStatus(s);
                const isSel = selected.has(s.id);
                const isLive = live?.program.id === s.id;
                const cls = [
                  styles.scheduleRow,
                  status === 'expired' && !isLive ? styles.scheduleRowExpired : '',
                  isSel ? styles.rowSelected : '',
                ]
                  .filter(Boolean)
                  .join(' ');
                const rowStyle = isLive
                  ? { borderLeftColor: 'var(--vn-success-solid)', background: 'var(--vn-success-subtle)' }
                  : { borderLeftColor: scheduleTypeColor(s.type) };
                const gap = i < rows.length - 1 ? gapBetween(s, rows[i + 1]) : 0;
                return (
                  <div key={s.id}>
                    <div className={cls} style={rowStyle}>
                      <Checkbox
                        checked={isSel}
                        onCheckedChange={() => toggleSelected(s.id)}
                        aria-label={`Select ${s.title || 'program'}`}
                      />
                      {/* Full start date+time so a program can be matched to its
                          absolute air time; end stays time-only to keep it compact. */}
                      <span className={styles.scheduleTime}>
                        {stampFromMs(s.start_time)} – {clockFromMs(s.end_time)}
                      </span>
                      <div className={styles.scheduleBody}>
                        <span className={styles.scheduleTitle}>{s.title || 'Untitled program'}</span>
                        <span className={styles.scheduleSub}>
                          {scheduleTypeLabel(s.type)} · {formatDurationHuman(s.duration)}
                          {s.description ? ` · ${s.description}` : ''}
                        </span>
                      </div>
                      {isLive ? (
                        <StatusPill tone="success">On air</StatusPill>
                      ) : (
                        <StatusPill tone={SCHEDULE_STATUS_TONE[status]}>{SCHEDULE_STATUS_LABEL[status]}</StatusPill>
                      )}
                      <div className={styles.scheduleActions}>
                        <IconButton
                          variant="ghost"
                          size="sm"
                          aria-label="Duplicate program"
                          disabled={create.isPending}
                          onClick={() => duplicate(s)}
                        >
                          <Copy size={14} />
                        </IconButton>
                        <IconButton
                          variant="ghost"
                          size="sm"
                          aria-label="Edit program"
                          onClick={() => setDialog({ mode: 'edit', schedule: s })}
                        >
                          <Pencil size={14} />
                        </IconButton>
                        <IconButton
                          variant="ghost"
                          size="sm"
                          aria-label="Delete program"
                          onClick={() => setPendingDelete(s)}
                        >
                          <Trash2 size={14} />
                        </IconButton>
                      </div>
                    </div>

                    {gap !== 0 && (
                      <div className={`${styles.gapDivider}${gap < 0 ? ` ${styles.gapOverlap}` : ''}`}>
                        <span className={styles.gapLine} />
                        {gap < 0
                          ? `Overlaps by ${formatDurationHuman(Math.abs(Math.round(gap / 1000)))}`
                          : `Gap ${formatDurationHuman(Math.round(gap / 1000))}`}
                        <span className={styles.gapLine} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardBody>
      </Card>

      {dialog && (
        <ScheduleDialog
          open
          onOpenChange={(o) => !o && setDialog(null)}
          serviceUuid={serviceUuid}
          channel={channel}
          mode={dialog.mode}
          schedule={dialog.schedule}
          lastEnd={rows[rows.length - 1]?.end_time}
        />
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Delete program?"
        description={`This removes “${pendingDelete?.title || 'this program'}” from the timeline. This action cannot be undone.`}
        confirmLabel="Delete"
        destructive
        loading={remove.isPending}
        onConfirm={() => pendingDelete && remove.mutate(pendingDelete.id)}
      />

      <ConfirmDialog
        open={bulkDeleteOpen}
        onOpenChange={(o) => !o && setBulkDeleteOpen(false)}
        title={`Delete ${selected.size} program${selected.size === 1 ? '' : 's'}?`}
        description="This removes the selected programs from the timeline. This action cannot be undone."
        confirmLabel="Delete"
        destructive
        loading={remove.isPending}
        onConfirm={bulkDelete}
      />
    </Section>
  );
}
